import { useRef, useState, useEffect, useLayoutEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useTheme, THEMES } from '../context/ThemeContext';
import { getAvatarEmoji } from '../constants/avatars';
import { SUPPORTED_LANGUAGES } from '../i18n';
import { VisualizerControls } from './AudioVisualizer';
import { MusicControls } from '../context/MusicContext';
import ThemeEditor from './ThemeEditor';
import { updateUserProfile } from '../services/firebase/users';
import { logError } from '../utils/logger';
import { deriveActionFg } from '../utils/color';

/**
 * Hardcoded colours for each built-in theme's selector buttons.
 * Kept in sync with the theme definitions in index.css.
 * Using inline styles here ensures the buttons are always readable regardless
 * of which custom theme is currently active.
 */
const THEME_BUTTON_COLORS = {
  standard:       { bg: '#1f2937', color: '#d1d5db', activeBg: '#3a88ed', activeColor: '#ffffff' },
  light:          { bg: '#f9fafb', color: '#374151', activeBg: '#6496c5', activeColor: '#ffffff' },
  black:          { bg: '#161616', color: '#d1d5db', activeBg: '#7c3aed', activeColor: '#ffffff' },
  'high-contrast':{ bg: '#000000', color: '#ffffff', activeBg: '#ffff00', activeColor: '#000000' },
};

const MAX_CUSTOM_THEMES = 8;

// ── Sync <html lang> with the active language ──────────────────────────────────
function HtmlLangSync() {
  const { i18n } = useTranslation();
  useEffect(() => {
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);
  return null;
}

// ── Settings menu ──────────────────────────────────────────────────────────────
function SettingsMenu({ userId, settingsExtra }) {
  const { t, i18n } = useTranslation();
  const {
    theme,
    setTheme,
    activeCustomThemeId,
    setActiveCustomThemeId,
    customThemes,
    setCustomThemes,
  } = useTheme();
  const [open, setOpen] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingTheme, setEditingTheme] = useState(null); // null = new theme
  const containerRef = useRef(null);
  const popupRef = useRef(null);

  // Clamp the popup so it never goes off the left edge of the viewport
  useLayoutEffect(() => {
    if (!open || !popupRef.current) return;
    const panel = popupRef.current;
    panel.style.transform = '';
    const rect = panel.getBoundingClientRect();
    if (rect.left < 8) {
      panel.style.transform = `translateX(${8 - rect.left}px)`;
    }
  }, [open]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function handleClick(e) {
      if (!containerRef.current?.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  // Close on Escape (only when editor is not open)
  useEffect(() => {
    if (!open || editorOpen) return;
    function handleKey(e) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, editorOpen]);

  function openNewTheme() {
    setEditingTheme(null);
    setEditorOpen(true);
  }

  function openEditTheme(ct) {
    setEditingTheme(ct);
    setEditorOpen(true);
  }

  async function handleSaveTheme(saved) {
    const existing = customThemes.find((ct) => ct.id === saved.id);
    const updated = existing
      ? customThemes.map((ct) => (ct.id === saved.id ? saved : ct))
      : [...customThemes, saved];

    // Optimistic update
    setCustomThemes(updated);
    setEditorOpen(false);

    // Activate the newly saved theme
    setActiveCustomThemeId(saved.id);

    // Persist to Firestore
    if (userId) {
      try {
        await updateUserProfile(userId, { customThemes: updated });
      } catch (err) {
        logError(err);
      }
    }
  }

  async function handleDeleteTheme(id) {
    const updated = customThemes.filter((ct) => ct.id !== id);
    setCustomThemes(updated);
    if (activeCustomThemeId === id) {
      setActiveCustomThemeId(null); // reverts to built-in theme
    }
    if (userId) {
      try {
        await updateUserProfile(userId, { customThemes: updated });
      } catch (err) {
        logError(err);
      }
    }
  }

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={t('nav.settingsLabel')}
        aria-expanded={open}
        aria-haspopup="dialog"
        className="p-1.5 rounded-md t-text-muted hover:t-text hover:t-bg-input transition-colors t-focus-ring"
        style={{ boxShadow: '0 0 0 1px rgba(127,127,127,0.4)' }}
      >
        {/* Heroicons: cog-6-tooth (mini) */}
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4" aria-hidden="true">
          <path fillRule="evenodd" d="M7.84 1.804A1 1 0 0 1 8.82 1h2.36a1 1 0 0 1 .98.804l.331 1.652a6.993 6.993 0 0 1 1.929 1.115l1.598-.54a1 1 0 0 1 1.186.447l1.18 2.044a1 1 0 0 1-.205 1.251l-1.267 1.113a7.047 7.047 0 0 1 0 2.228l1.267 1.113a1 1 0 0 1 .206 1.25l-1.18 2.045a1 1 0 0 1-1.187.447l-1.598-.54a6.993 6.993 0 0 1-1.929 1.115l-.33 1.652a1 1 0 0 1-.98.804H8.82a1 1 0 0 1-.98-.804l-.331-1.652a6.993 6.993 0 0 1-1.929-1.115l-1.598.54a1 1 0 0 1-1.186-.447l-1.18-2.044a1 1 0 0 1 .205-1.251l1.267-1.114a7.05 7.05 0 0 1 0-2.227L1.821 7.773a1 1 0 0 1-.206-1.25l1.18-2.045a1 1 0 0 1 1.187-.447l1.598.54A6.992 6.992 0 0 1 7.51 3.456L7.84 1.804ZM10 13a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z" clipRule="evenodd" />
        </svg>
      </button>

      {open && (
        <div
          ref={popupRef}
          role="dialog"
          aria-label={t('nav.settings')}
          className="absolute right-0 mt-2 w-72 t-bg-surface t-border border rounded-xl shadow-xl z-50 overflow-hidden"
        >
          {/* Language */}
          <div className="px-4 pt-4 pb-3 space-y-2.5">
            <p className="text-xs font-semibold t-text-disabled uppercase tracking-widest">
              {t('nav.settingsSections.language')}
            </p>
            <div role="radiogroup" aria-label={t('nav.settingsSections.language')} className="flex gap-1 flex-wrap">
              {SUPPORTED_LANGUAGES.map((lang) => (
                <button
                  key={lang.code}
                  type="button"
                  role="radio"
                  aria-checked={i18n.language === lang.code}
                  onClick={() => i18n.changeLanguage(lang.code)}
                  className={`text-xs px-2.5 py-1 rounded-full font-medium transition-colors t-focus-ring ${
                    i18n.language === lang.code
                      ? 't-btn-primary'
                      : 't-bg-input t-text-secondary hover:opacity-80'
                  }`}
                >
                  {lang.name}
                </button>
              ))}
            </div>
          </div>

          <div className="t-border border-t" />

          {/* Theme — built-in presets */}
          <div className="px-4 pt-3 pb-3 space-y-2.5">
            <p className="text-xs font-semibold t-text-disabled uppercase tracking-widest">
              {t('nav.settingsSections.theme')}
            </p>
            <div role="radiogroup" aria-label={t('nav.themeLabel')} className="flex gap-1 flex-wrap">
              {THEMES.map((themeKey) => {
                const isActive = !activeCustomThemeId && theme === themeKey;
                const colors = THEME_BUTTON_COLORS[themeKey];
                return (
                  <button
                    key={themeKey}
                    type="button"
                    role="radio"
                    aria-checked={isActive}
                    onClick={() => setTheme(themeKey)}
                    className="text-xs px-2.5 py-1 rounded-full font-medium transition-opacity t-focus-ring hover:opacity-80"
                    style={colors ? {
                      backgroundColor: isActive ? colors.activeBg : colors.bg,
                      color: isActive ? colors.activeColor : colors.color,
                      border: '1px solid rgba(127,127,127,0.25)',
                    } : undefined}
                  >
                    {t(`nav.themes.${themeKey}`)}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Custom themes section (only shown to logged-in users with userId) */}
          {userId && (
            <>
              <div className="t-border border-t" />
              <div className="px-4 pt-3 pb-3 space-y-2.5">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold t-text-disabled uppercase tracking-widest">
                    {t('nav.settingsSections.customThemes')}
                  </p>
                  {customThemes.length < MAX_CUSTOM_THEMES && (
                    <button
                      type="button"
                      onClick={openNewTheme}
                      className="text-xs t-text-accent hover:t-text transition-colors t-focus-ring rounded px-1"
                      aria-label={t('customTheme.newTheme')}
                    >
                      + {t('customTheme.newTheme')}
                    </button>
                  )}
                </div>

                {customThemes.length === 0 && (
                  <p className="text-xs t-text-disabled italic">{t('customTheme.newTheme')}</p>
                )}

                {customThemes.map((ct) => {
                  const isActive = activeCustomThemeId === ct.id;
                  // Derive inline styles from the theme's own vars so the button is
                  // always readable regardless of the currently active custom theme.
                  const btnBg = isActive
                    ? (ct.vars['--c-action-bg'] || '#3a88ed')
                    : (ct.vars['--c-bg-input'] || ct.vars['--c-bg-surface'] || '#1f2937');
                  const btnColor = deriveActionFg(btnBg);
                  return (
                    <div key={ct.id} className="flex items-center gap-1.5">
                      {/* Colour swatch strip */}
                      <div className="flex rounded overflow-hidden shrink-0" aria-hidden="true">
                        {['--c-bg-page', '--c-bg-surface', '--c-action-bg', '--c-text'].map((v) => (
                          <span
                            key={v}
                            className="w-3 h-4 inline-block"
                            style={{ backgroundColor: ct.vars[v] || '#888' }}
                          />
                        ))}
                      </div>

                      {/* Activate button — uses the theme's own colours for immediate visual feedback
                          and always-readable text (deriveActionFg picks black or white). */}
                      <button
                        type="button"
                        role="radio"
                        aria-checked={isActive}
                        onClick={() => isActive ? setActiveCustomThemeId(null) : setActiveCustomThemeId(ct.id)}
                        className="flex-1 text-xs px-2 py-1 rounded-full font-medium text-left truncate transition-opacity t-focus-ring hover:opacity-80"
                        style={{
                          backgroundColor: btnBg,
                          color: btnColor,
                          border: '1px solid rgba(127,127,127,0.25)',
                        }}
                        title={isActive ? t('customTheme.deactivateTheme') : t('customTheme.activateTheme')}
                      >
                        {ct.name}
                      </button>

                      {/* Edit */}
                      <button
                        type="button"
                        onClick={() => openEditTheme(ct)}
                        className="text-xs t-text-muted hover:t-text transition-colors t-focus-ring rounded p-0.5 shrink-0"
                        aria-label={`${t('customTheme.editTheme')}: ${ct.name}`}
                      >
                        ✏
                      </button>

                      {/* Delete */}
                      <button
                        type="button"
                        onClick={() => handleDeleteTheme(ct.id)}
                        className="text-xs t-text-muted hover:t-text-warning transition-colors t-focus-ring rounded p-0.5 shrink-0"
                        aria-label={`${t('customTheme.deleteTheme')}: ${ct.name}`}
                      >
                        ✕
                      </button>
                    </div>
                  );
                })}

                {customThemes.length >= MAX_CUSTOM_THEMES && (
                  <p className="text-xs t-text-muted">{t('customTheme.maxThemes')}</p>
                )}
              </div>
            </>
          )}

          <div className="t-border border-t" />

          {/* Visualiser */}
          <div className="px-4 pt-3 pb-3 space-y-2.5">
            <p className="text-xs font-semibold t-text-disabled uppercase tracking-widest">
              {t('nav.settingsSections.visualiser')}
            </p>
            <VisualizerControls onClose={() => setOpen(false)} />
          </div>

          <div className="t-border border-t" />

          {/* Music */}
          <div className="px-4 pt-3 pb-4 space-y-2.5">
            <p className="text-xs font-semibold t-text-disabled uppercase tracking-widest">
              {t('nav.settingsSections.music')}
            </p>
            <MusicControls />
          </div>

          {/* Extra settings injected by parent (e.g. game button visibility toggles) */}
          {settingsExtra}
        </div>
      )}

      {/* Theme editor modal (rendered in portal) */}
      {editorOpen && (
        <ThemeEditor
          theme={editingTheme}
          onSave={handleSaveTheme}
          onClose={() => setEditorOpen(false)}
        />
      )}
    </div>
  );
}

// ── Layout ─────────────────────────────────────────────────────────────────────
export default function Layout({ children, hideHeader = false, settingsExtra = null }) {
  const { user, profile, logout, isPremium } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setCustomThemes } = useTheme();

  // Sync Firestore custom themes into ThemeContext whenever profile changes
  useEffect(() => {
    setCustomThemes(profile?.customThemes ?? []);
  }, [profile?.customThemes, setCustomThemes]);

  const [confirmSignOut, setConfirmSignOut] = useState(false);

  async function handleLogout() {
    await logout();
    navigate('/login');
  }

  return (
    <div className="h-dvh t-bg-page t-text flex flex-col overflow-hidden">
      <HtmlLangSync />
      {!hideHeader && <header className="t-border border-b px-4 py-3 flex items-center justify-between">
        <Link
          to="/lobby"
          className="text-lg font-bold tracking-tight t-text hover:t-text-accent transition-colors t-focus-ring rounded"
        >
          🎵 thatmusicgame
        </Link>

        <div className="flex items-center gap-3">
          {/* Settings cog */}
          <SettingsMenu userId={user?.uid} settingsExtra={settingsExtra} />

          {/* Profile link */}
          {user && (
            <>
              {profile?.username ? (
                <Link
                  to={`/profile/${profile.username}`}
                  className="flex items-center gap-2 group t-focus-ring rounded"
                  aria-label={t('nav.viewProfile', { username: profile.username })}
                >
                  <span
                    className="w-7 h-7 rounded-full t-bg-input flex items-center justify-center text-sm leading-none"
                    aria-hidden="true"
                  >
                    {getAvatarEmoji(profile.avatarKey)}
                  </span>
                  <span className="text-sm t-text-muted group-hover:t-text transition-colors hidden sm:block">
                    {profile.username}
                  </span>
                </Link>
              ) : (
                <span className="text-sm t-text-muted hidden sm:block">{user.displayName}</span>
              )}

              {/* Sign out */}
              {confirmSignOut ? (
                <div className="flex items-center gap-1">
                  <span className="text-sm t-text-muted">{t('nav.signOutConfirm')}</span>
                  <button
                    onClick={handleLogout}
                    className="text-sm t-text-error hover:t-text transition-colors px-2 py-1.5 rounded t-focus-ring"
                  >
                    {t('nav.signOutYes')}
                  </button>
                  <button
                    onClick={() => setConfirmSignOut(false)}
                    className="text-sm t-text-muted hover:t-text transition-colors px-2 py-1.5 rounded t-focus-ring"
                  >
                    {t('nav.signOutCancel')}
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setConfirmSignOut(true)}
                  className="text-sm t-text-muted hover:t-text transition-colors px-2 py-1.5 rounded t-focus-ring"
                >
                  {t('nav.signOut')}
                </button>
              )}
            </>
          )}
        </div>
      </header>}

      <main id="main-content" className="flex-1 flex flex-col min-h-0 overflow-y-auto">{children}</main>
    </div>
  );
}
